import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Form, Input, Button, Alert, Modal } from 'antd';
import { updateBusinessList } from '../../../services/MasterApi';
import { STRIPE_PAYMENT_INTENT_URL, STRIPE_CONFIRM_URL } from '../../../services/const';
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCreditCard, FaLock, FaInfoCircle, FaCheckCircle, FaSpinner } from 'react-icons/fa';

export default function CheckoutForm({ total }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [cardholderName, setCardholderName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cardBrand, setCardBrand] = useState('unknown');
  
  // Element completeness state
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  // Focus/Touch states to show red borders on inputs
  const [cardholderNameTouched, setCardholderNameTouched] = useState(false);
  const [cardNumberTouched, setCardNumberTouched] = useState(false);
  const [cardExpiryTouched, setCardExpiryTouched] = useState(false);
  const [cardCvcTouched, setCardCvcTouched] = useState(false);
  const [postalCodeTouched, setPostalCodeTouched] = useState(false);
  
  const [showAlert, setShowAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [paymentStatusText, setPaymentStatusText] = useState('');
  const [errors, setErrors] = useState({});

  const transactionId = localStorage.getItem("transactionId");
  const business = useSelector((state) => state.business);
  const businessTypeId = business?.businessTypeId;
  const fullName = business?.primaryContact?.fullName;
  const email = business?.primaryContact?.email;

  // Stripe element options for styling
  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        color: '#2d3748',
        letterSpacing: '0.025em',
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: '#a0aec0',
        },
      },
      invalid: {
        color: '#e53e3e',
      },
    },
  };

  const handleCardNumberChange = (event) => {
    setCardBrand(event.brand || 'unknown');
    setCardNumberComplete(event.complete);
    if (event.complete) {
      setCardNumberTouched(false);
      setErrors((prev) => ({ ...prev, cardNumber: null }));
      setError(null);
    } else if (event.error) {
      setErrors((prev) => ({ ...prev, cardNumber: event.error.message }));
    }
  };

  const handleCardExpiryChange = (event) => {
    setCardExpiryComplete(event.complete);
    if (event.complete) {
      setCardExpiryTouched(false);
      setErrors((prev) => ({ ...prev, cardExpiry: null }));
      setError(null);
    } else if (event.error) {
      setErrors((prev) => ({ ...prev, cardExpiry: event.error.message }));
    }
  };

  const handleCardCvcChange = (event) => {
    setCardCvcComplete(event.complete);
    if (event.complete) {
      setCardCvcTouched(false);
      setErrors((prev) => ({ ...prev, cardCvc: null }));
      setError(null);
    } else if (event.error) {
      setErrors((prev) => ({ ...prev, cardCvc: event.error.message }));
    }
  };

  const getCardIcon = () => {
    switch (cardBrand) {
      case 'visa':
        return <FaCcVisa className="card-brand-icon visa" />;
      case 'mastercard':
        return <FaCcMastercard className="card-brand-icon mastercard" />;
      case 'amex':
        return <FaCcAmex className="card-brand-icon amex" />;
      case 'discover':
        return <FaCcDiscover className="card-brand-icon discover" />;
      default:
        return <FaCreditCard className="card-brand-icon generic" />;
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setErrors({});
    setPaymentStatusText('');

    // Trigger visual validation borders for all fields
    setCardholderNameTouched(true);
    setCardNumberTouched(true);
    setCardExpiryTouched(true);
    setCardCvcTouched(true);
    setPostalCodeTouched(true);

    const errs = {};
    if (!cardholderName.trim()) {
      errs.cardholderName = "Cardholder name is required.";
    }

    if (!cardNumberComplete) {
      errs.cardNumber = "Card number is incomplete.";
    }

    if (!cardExpiryComplete) {
      errs.cardExpiry = "Expiration date is incomplete.";
    }

    if (!cardCvcComplete) {
      errs.cardCvc = "CVC/CVV is incomplete.";
    }

    if (!postalCode.trim()) {
      errs.postalCode = "Billing ZIP/postal code is required.";
    } else if (!/^[a-zA-Z0-9 -]{3,10}$/.test(postalCode.trim())) {
      errs.postalCode = "Please enter a valid ZIP/postal code.";
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Check online status before proceeding
    if (!navigator.onLine) {
      setError("No internet connection detected. Please check your network and try again.");
      return;
    }

    setProcessing(true);
    setPaymentStatusText('1. Connecting to payment server...');

    // Timeout alert for slow network
    const slowNetworkTimer = setTimeout(() => {
      setPaymentStatusText('Your bank is taking a bit longer than usual. Please do not close or refresh this page...');
    }, 8000);

    const clearTimer = () => clearTimeout(slowNetworkTimer);

    // Call Stripe confirmation endpoint and redirect
    const finalizePayment = async (chargeId) => {
      clearTimer();
      setPaymentStatusText('3. Finalizing your order records...');
      const txId = parseInt(transactionId);

      try {
        // 1. Direct call to server Stripe confirmation endpoint (unauthenticated, verified server-side with Stripe)
        const confirmResponse = await fetch(STRIPE_CONFIRM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transactionId: txId,
            paymentIntentId: chargeId,
          }),
        });

        if (confirmResponse.ok) {
          console.log("Confirmed payment with Stripe backend controller successfully");
        } else {
          // Fallback to updateBusinessList
          const paymentData = {
            businessTransactionId: txId,
            businessTypeId: parseInt(businessTypeId),
            transactionStatusId: 3,
            primaryContact: {
              fullName: fullName,
              email: email
            },
            payment: {
              paymentModeId: 2,
              paymentStructureId: 2,
              paymentStatusId: 1,
              paymentTransactionId: chargeId,
              payer: {
                id: `usr_${Math.random().toString(36).substr(2, 9)}`,
                name: cardholderName,
                email: email || "billing@oktobiz.com",
                countryId: 1,
                updateDate: Date.now(),
                createDate: Date.now()
              },
              payee: {
                email: "billing@oktobiz.com",
                merchantId: "acct_stripe_oktobiz"
              }
            }
          };
          await updateBusinessList(paymentData).catch(e => console.warn("Fallback update error:", e));
        }

        localStorage.removeItem("stripeAccess");
        localStorage.removeItem("transactionId");
        
        setPaymentStatusText('');
        setProcessing(false);

        const modal = Modal.success({
          title: 'Payment Successful!',
          content: 'Your transaction has been processed successfully. You are now being redirected to the confirmation page.',
          okText: 'View Confirmation',
          onOk: () => {
            clearTimeout(autoRedirect);
            navigate("/confirm");
          }
        });

        const autoRedirect = setTimeout(() => {
          modal.destroy();
          navigate("/confirm");
        }, 2500);
      } catch (err) {
        console.error("Error updating business listing in DB: ", err);
        setPaymentStatusText('');
        setProcessing(false);

        const modal = Modal.success({
          title: 'Payment Successful!',
          content: 'Your transaction has been processed successfully. You are now being redirected to the confirmation page.',
          okText: 'View Confirmation',
          onOk: () => {
            clearTimeout(autoRedirect);
            navigate("/confirm");
          }
        });

        const autoRedirect = setTimeout(() => {
          modal.destroy();
          navigate("/confirm");
        }, 2500);
      }
    };

    // Try real API payment creation
    try {
      if (!stripe || !elements) {
        clearTimer();
        throw new Error("Stripe elements not loaded.");
      }

      try {
        setPaymentStatusText('1. Initiating secure checkout intent...');
        const response = await fetch(STRIPE_PAYMENT_INTENT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ transactionId: parseInt(transactionId) }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || "Failed to initiate payment");
        }
        const data = await response.json();
        if (data.error || !data.content || !data.content.clientSecret) {
          throw new Error(data.message || "Invalid client secret received from server");
        }

        const { clientSecret, mock } = data.content;

        // In live mode, do not accept fake/mock intents
        if (mock || clientSecret.startsWith('pi_mock_')) {
          throw new Error('Server returned mock payment intent. Please check server Stripe live keys.');
        }

        // ── REAL STRIPE FLOW ─────────────────────────────────────────────────
        setPaymentStatusText('2. Confirming card credentials with Stripe...');

        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              name: cardholderName,
              address: { postal_code: postalCode },
            },
          },
        });

        clearTimer();

        if (result.error) {
          setError(result.error.message);
          setProcessing(false);
          setPaymentStatusText('');
        } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
          setPaymentStatusText('3. Payment successful! Updating your business profile...');
          await finalizePayment(result.paymentIntent.id);
        } else if (result.paymentIntent && result.paymentIntent.status === 'processing') {
          setPaymentStatusText('3. Payment is processing! Updating your business profile...');
          await finalizePayment(result.paymentIntent.id);
        } else if (result.paymentIntent && result.paymentIntent.status === 'requires_action') {
          setError('Additional 3D Secure verification was requested by your bank. Please try again.');
          setProcessing(false);
          setPaymentStatusText('');
        } else {
          setError(result.paymentIntent ? `Payment status: ${result.paymentIntent.status}. Please try again.` : 'Payment incomplete. Please try again.');
          setProcessing(false);
          setPaymentStatusText('');
        }

      } catch (backendError) {
        clearTimer();
        console.error("Payment initiation error: ", backendError);
        setError(backendError.message || "Failed to initiate payment. Please try again.");
        setProcessing(false);
        setPaymentStatusText('');
      }

    } catch (e) {
      clearTimer();
      console.error("General Stripe error: ", e);
      if (e.message && e.message.includes("already been paid")) {
        localStorage.removeItem("stripeAccess");
        localStorage.removeItem("transactionId");
        setPaymentStatusText('Already paid! Redirecting you to home page...');
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setError(e.message || "An unexpected error occurred during processing. Please try again.");
        setProcessing(false);
        setPaymentStatusText('');
      }
    }
  };

  return (
    <div className="checkout-form-container">
      {/* Credit Card Input Form */}
      <Form
        layout="vertical"
        onFinish={handleSubmit}
        className="payment-form"
        requiredMark={false}
      >
        <Form.Item
          label="Cardholder Name"
          required
          validateStatus={errors.cardholderName ? "error" : (cardholderNameTouched && !cardholderName.trim() ? "error" : "")}
        >
          <Input
            id="cardholder-name"
            placeholder="John Doe"
            value={cardholderName}
            onChange={(e) => {
              setCardholderName(e.target.value);
              setErrors((prev) => ({ ...prev, cardholderName: null }));
              if (e.target.value.trim()) {
                setCardholderNameTouched(false);
                setError(null);
              }
            }}
            disabled={processing}
            size="large"
          />
          {errors.cardholderName && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '0px' }}>{errors.cardholderName}</p>}
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <span>Card Number</span>
              <div className="card-brand-inline-logo" style={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                {getCardIcon()}
              </div>
            </div>
          }
          required
          validateStatus={errors.cardNumber ? "error" : (cardNumberTouched && !cardNumberComplete ? "error" : "")}
        >
          <div className={`stripe-input-wrapper ${errors.cardNumber ? "stripe-input-error" : (cardNumberTouched && !cardNumberComplete ? "stripe-input-error" : "")}`}>
            <CardNumberElement
              options={ELEMENT_OPTIONS}
              onChange={handleCardNumberChange}
            />
          </div>
          {errors.cardNumber && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '0px' }}>{errors.cardNumber}</p>}
        </Form.Item>

        <div className="form-row">
          <Form.Item
            label="Expiration Date"
            required
            className="half-width"
            validateStatus={errors.cardExpiry ? "error" : (cardExpiryTouched && !cardExpiryComplete ? "error" : "")}
          >
            <div className={`stripe-input-wrapper ${errors.cardExpiry ? "stripe-input-error" : (cardExpiryTouched && !cardExpiryComplete ? "stripe-input-error" : "")}`}>
              <CardExpiryElement
                options={ELEMENT_OPTIONS}
                onChange={handleCardExpiryChange}
              />
            </div>
            {errors.cardExpiry && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '0px' }}>{errors.cardExpiry}</p>}
          </Form.Item>

          <Form.Item
            label="CVC / CVV"
            required
            className="half-width"
            validateStatus={errors.cardCvc ? "error" : (cardCvcTouched && !cardCvcComplete ? "error" : "")}
          >
            <div className={`stripe-input-wrapper ${errors.cardCvc ? "stripe-input-error" : (cardCvcTouched && !cardCvcComplete ? "stripe-input-error" : "")}`}>
              <CardCvcElement
                options={ELEMENT_OPTIONS}
                onChange={handleCardCvcChange}
              />
            </div>
            {errors.cardCvc && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '0px' }}>{errors.cardCvc}</p>}
          </Form.Item>
        </div>

        <Form.Item
          label="Billing ZIP / Postal Code"
          required
          validateStatus={errors.postalCode ? "error" : (postalCodeTouched && (!postalCode.trim() || !/^[a-zA-Z0-9 -]{3,10}$/.test(postalCode.trim())) ? "error" : "")}
        >
          <Input
            id="billing-zip"
            placeholder="90210"
            value={postalCode}
            onChange={(e) => {
              setPostalCode(e.target.value);
              setErrors((prev) => ({ ...prev, postalCode: null }));
              if (e.target.value.trim() && /^[a-zA-Z0-9 -]{3,10}$/.test(e.target.value.trim())) {
                setPostalCodeTouched(false);
                setError(null);
              }
            }}
            disabled={processing}
            size="large"
          />
          {errors.postalCode && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '0px' }}>{errors.postalCode}</p>}
        </Form.Item>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: '15px' }}
          />
        )}

        <Button
          type="primary"
          htmlType="submit"
          block
          loading={processing}
          disabled={processing}
          size="large"
          className="pay-submit-btn"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center', width: '100%' }}>
            {!processing && <FaLock className="lock-icon" />}
            Secure Pay ${total.toFixed(2)}
          </span>
        </Button>

        {paymentStatusText && (
          <div className="payment-status-message" style={{
            marginTop: '12px',
            fontSize: '14px',
            color: '#2b6cb0',
            textAlign: 'center',
            fontWeight: '500',
            backgroundColor: '#ebf8ff',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #bee3f8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {paymentStatusText.includes("successful") || paymentStatusText.includes("Mock payment success") ? (
              <FaCheckCircle style={{ color: '#38a169', fontSize: '16px' }} />
            ) : paymentStatusText.includes("Already paid") ? (
              <FaInfoCircle style={{ color: '#2b6cb0', fontSize: '16px' }} />
            ) : (
              <FaSpinner className="spin-animation" style={{ animation: 'spin-loader 1s linear infinite', fontSize: '16px' }} />
            )}
            <span>{paymentStatusText}</span>
          </div>
        )}

        <p className="payment-disclaimer">
          Your payment information is secured with 256-bit SSL encryption. We do not store credit card credentials.
        </p>
      </Form>

      {showAlert && (
        <Alert
          message={alertMessage}
          type={alertSeverity === 'success' ? 'success' : 'error'}
          showIcon
          closable
          onClose={() => setShowAlert(false)}
          style={{ marginTop: '15px', width: '100%' }}
        />
      )}
    </div>
  );
}
